import { useTheme } from '@mui/material';
import { useReactFlow } from '@xyflow/react';
import { orderBy } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';

import {
  FlowChartProps,
  Node,
  useFlowChartContext,
} from '@/components/diagrams/FlowChart';
import { useHelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { SECTION_LABEL_NODE_PADDING } from '@/components/diagrams/FlowChart/constants';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';
import { useSectionNodesBounds } from '@/components/diagrams/FlowChart/nodes/SectionLabelNode/hooks';
import {
  isTileGroupNode,
  isTileNode,
} from '@/components/diagrams/FlowChart/utils/nodes';
import { isTextNoteNode } from '@/components/diagrams/FlowChart/utils/textNoteUtils';
import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { useOptionalDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import {
  createTemporaryGroupId,
  VisualizationGroup,
} from '@/modules/graphViz/graphVizGroup/grouping';
import { AfterDeath } from '@/types/schema';

import { useEntityDiagramContext } from '../contexts/entityDiagram.context';
import {
  getDispositionsInterSectionPadding,
  sectionPaddingEqual,
} from '../utils/getDispositionsInterSectionPadding';
import { isTileSelectable } from '../utils/isTileSelectable';

export function useFlowChartProps(
  onCreateGroup: (group: VisualizationGroup, onCancel: () => void) => void,
  renderGroupSettings: (
    group: VisualizationGroup,
    onClose: () => void
  ) => JSX.Element
) {
  const theme = useTheme();
  const isViewOnly = useViewOnly();
  const { getNodes } = useReactFlow();
  const { isInteractive } = useIsInteractive();
  const { state, dispatch, getState } = useEntityDiagramContext();
  const {
    dropTargetNode,
    __internal: { setIsDirty },
  } = useFlowChartContext();
  const { helperLineVertical, helperLineHorizontal, applyHelperLinesToNodes } =
    useHelperLines();
  // Recompute on every node/container/variant change, then stabilize the
  // reference: when the computed value is equal to the previous one, return
  // the previous reference so downstream `useCallback` deps (e.g. inside
  // `useSectionNodesBounds`) don't bust on every render — a fresh array
  // with identical contents would otherwise cascade and recreate
  // `onNodesChange`.
  const nextSectionPadding = useMemo(
    () =>
      getDispositionsInterSectionPadding({
        nodes: state.nodes,
        edges: state.edges,
        isInHalfWidthContainer: state.isInHalfWidthContainer,
        entityDiagramVariant: state.entityDiagramVariant,
        targetAspectRatio: state.containerAspectRatio,
      }),
    [
      state.nodes,
      state.edges,
      state.isInHalfWidthContainer,
      state.entityDiagramVariant,
      state.containerAspectRatio,
    ]
  );
  const stablePaddingRef = useRef(nextSectionPadding);
  if (!sectionPaddingEqual(stablePaddingRef.current, nextSectionPadding)) {
    stablePaddingRef.current = nextSectionPadding;
  }
  const { interSectionPadding, labelPadding } = stablePaddingRef.current;
  const { applySectionBoundsToNodes } = useSectionNodesBounds({
    padding: SECTION_LABEL_NODE_PADDING,
    labelPadding,
    interSectionPadding,
  });
  const { setSelectedNode } = useOptionalDispositiveDiagramSyncContext() ?? {
    setSelectedNode: undefined,
  };

  const onNodeDragStart = useCallback<
    NonNullable<FlowChartProps['onNodeDragStart']>
  >(() => {
    dispatch({ type: 'NODE_DRAG_START' });
  }, [dispatch]);

  const onNodeDragStop = useCallback<
    NonNullable<FlowChartProps['onNodeDragStop']>
  >(
    (_event, node) => {
      dispatch({ type: 'NODE_DRAG_STOP' });

      if (!dropTargetNode) return;
      const targetNode = dropTargetNode;
      setTimeout(() => {
        const currentState = getState();
        const rawId = node.id.split(':').slice(1).join(':');
        const targetRawId = targetNode.id.split(':').slice(1).join(':');
        const namespace = String(node.data.sectionLabelId ?? '');
        const targetGroupId = targetNode.data.visualizationGroupId;
        const existing =
          typeof targetGroupId === 'string'
            ? currentState.visualizationGroups.find(
                (group) => group.id === targetGroupId
              )
            : undefined;
        if (!existing) {
          onCreateGroup(
            {
              id: createTemporaryGroupId(),
              displayName: '',
              namespace,
              nodeIds: [targetRawId, rawId],
            },
            () => dispatch({ type: 'UNDO_HISTORY' })
          );
          return;
        }
        const group = { ...existing, nodeIds: [...existing.nodeIds, rawId] };
        dispatch({
          type: 'SET_VISUALIZATION_GROUPS',
          mergePreviousDrag: true,
          groups: [
            ...currentState.visualizationGroups.filter(
              (item) => item.id !== group.id
            ),
            group,
          ],
        });
        setIsDirty(true);
      });
    },
    [dispatch, dropTargetNode, getState, onCreateGroup, setIsDirty]
  );

  const onNodeMouseEnter = useCallback<
    NonNullable<FlowChartProps['onNodeMouseEnter']>
  >(
    (event, node) => {
      // No relevant code paths when non tile node (section group), or in edit mode
      if (node.type !== 'tile' || isInteractive) return;
      dispatch({ type: 'NODE_MOUSE_ENTER', event, node });
    },
    [dispatch, isInteractive]
  );

  const onNodeMouseLeave = useCallback<
    NonNullable<FlowChartProps['onNodeMouseLeave']>
  >(
    (event, node) => {
      // No relevant code paths when non tile node (section group), or in edit mode
      if (node.type !== 'tile' || isInteractive) return;
      dispatch({ type: 'NODE_MOUSE_LEAVE', event, node });
    },
    [dispatch, isInteractive]
  );

  const onNodesChange = useCallback<
    NonNullable<FlowChartProps['onNodesChange']>
  >(
    (changes) => {
      // Section-bound calculations mutate positions in place. Clone nested
      // mutable fields so those calculations cannot alter the reducer's
      // pre-change nodes before history captures them.
      const currentNodes = (getNodes() as Node[]).map((node) => ({
        ...node,
        position: { ...node.position },
        ...(node.style ? { style: { ...node.style } } : {}),
      }));
      const nodes = applyHelperLinesToNodes(
        changes,
        applySectionBoundsToNodes(changes, currentNodes)
      );

      dispatch({ type: 'NODES_CHANGED', nodes, changes, isInteractive });
    },
    [
      applyHelperLinesToNodes,
      applySectionBoundsToNodes,
      dispatch,
      getNodes,
      isInteractive,
    ]
  );

  const onEdgeMouseEnter = useCallback<
    NonNullable<FlowChartProps['onEdgeMouseEnter']>
  >(
    (event, edge) => {
      // No relevant code paths in edit mode
      if (isInteractive) return;

      /**
       * Note: for some reason, edge mouse enter events can happen in rapid succession.
       * This doesn't occur for nodes, maybe because the edge hover area is so small?
       * Either way, we guard against it here as a stop gap from many events firing.
       */
      if (getState().highlightedIds.has(edge.id)) return;

      dispatch({ type: 'EDGE_MOUSE_ENTER', event, edge });
    },
    [dispatch, getState, isInteractive]
  );

  const onEdgeMouseLeave = useCallback<
    NonNullable<FlowChartProps['onEdgeMouseLeave']>
  >(
    (event, edge) => {
      // No relevant code paths in edit mode
      if (isInteractive) return;
      dispatch({ type: 'EDGE_MOUSE_LEAVE', event, edge });
    },
    [dispatch, isInteractive]
  );

  const onPaneClick = useCallback<
    NonNullable<FlowChartProps['onPaneClick']>
  >(() => {
    if (getState().derivedData.isSummaryPanelOpen) {
      dispatch({ type: 'CLOSE_SUMMARY_PANEL' });
    }
  }, [dispatch, getState]);

  const onDeleteTextNote = useCallback(
    (nodeId: string) => {
      dispatch({ type: 'DELETE_TEXT_NOTE', nodeId });
    },
    [dispatch]
  );

  const onTextNoteNotesChange = useCallback(
    (nodeId: string, notes: string) => {
      dispatch({ type: 'UPDATE_TEXT_NOTE', nodeId, notes });
    },
    [dispatch]
  );

  const onNodeResizeStart = useCallback(() => {
    dispatch({ type: 'NODE_RESIZE_START' });
  }, [dispatch]);

  const onNodeResizeEnd = useCallback(() => {
    dispatch({ type: 'NODE_RESIZE_END' });
  }, [dispatch]);

  const nodesWithCallbacks = useMemo(
    () =>
      state.nodes.map((node): Node => {
        if (isTextNoteNode(node)) {
          return {
            ...node,
            data: {
              ...node.data,
              onDelete: onDeleteTextNote,
              onNotesChange: onTextNoteNotesChange,
              onNodeResizeStart,
              onNodeResizeEnd,
            },
          };
        }

        if (!isTileNode(node)) return node;

        const group = state.visualizationGroups.find(
          (item) => item.id === node.data.visualizationGroupId
        );
        return {
          ...node,
          data: {
            ...node.data,
            ...(group && {
              editModal: ({ onClose }) => renderGroupSettings(group, onClose),
            }),
            onNodeResizeStart,
            onNodeResizeEnd,
          },
        };
      }),
    [
      state.nodes,
      state.visualizationGroups,
      renderGroupSettings,
      onDeleteTextNote,
      onTextNoteNotesChange,
      onNodeResizeStart,
      onNodeResizeEnd,
    ]
  );

  // Draw the highlighted edges on top of the others
  const orderedEdges = orderBy(state.edges, (edge) => edge.data?.highlight);

  const onNodeClick = useCallback<NonNullable<FlowChartProps['onNodeClick']>>(
    (_event, node) => {
      if (isInteractive || isTileGroupNode(node) || node.type !== 'tile') {
        return;
      }

      if (node.id === state.summaryNodeId) {
        dispatch({ type: 'CLOSE_SUMMARY_PANEL' });
      } else if (!setSelectedNode) {
        dispatch({ type: 'OPEN_SUMMARY_PANEL', nodeId: node.id });
      } else if (setSelectedNode) {
        const [afterDeathString, ...rest] = node.id.split(':');
        const pulid = rest.join(':');
        if (!afterDeathString || !pulid) return;
        const afterDeath = afterDeathString as AfterDeath;

        const selectable = isTileSelectable({
          pulid,
          afterDeath,
          primaryClients: state.primaryClients,
          isTwoClientHousehold: state.isTwoClientHousehold,
          isOnDispositionsFormDiagram: !!state.isOnDispositionsFormDiagram,
        });
        if (!selectable) return;

        setSelectedNode({ pulid, afterDeath, node });
      }
    },
    [
      dispatch,
      isInteractive,
      setSelectedNode,
      state.summaryNodeId,
      state.isTwoClientHousehold,
      state.primaryClients,
      state.isOnDispositionsFormDiagram,
    ]
  );

  const flowChartProps: FlowChartProps = {
    style: {
      opacity: state.opacity,
      transition: isViewOnly
        ? undefined
        : theme.transitions.create(['opacity'], {
            duration: theme.transitions.duration.shortest,
            easing: theme.transitions.easing.easeInOut,
          }),
    },
    nodes: nodesWithCallbacks,
    edges: orderedEdges,
    onNodeDragStart,
    onNodeDragStop,
    onNodeClick,
    onPaneClick,
    onNodesChange,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    getState,
    ...(setSelectedNode ? { multiSelectionKeyCode: null } : {}),
  };

  return { flowChartProps, helperLineHorizontal, helperLineVertical };
}
